import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Box, Flex, Text, Table, Badge, Card } from "@radix-ui/themes"
import { restaurantPortalAPI } from "../../../services/restaurantApiClient"
import { useRestaurantAuth } from "../../../contexts/RestaurantAuthContext"
import ReportLayout from "./ReportLayout"
import LoadingSpinner from "../dashboard/LoadingSpinner"
import { Input } from "../../ui/input"
import { Label } from "../../ui/label"

interface CompData {
    type: string
    orderNumber: string
    amount: number
    cashier: string
    date: string
    reason: string
}

interface VoidsCompsData {
    period: { start: string; end: string }
    totalLostRevenue: number
    comps: CompData[]
}

const VoidsCompsReport: React.FC = () => {
    const { restaurantId } = useParams()
    const { user } = useRestaurantAuth()
    const navigate = useNavigate()
    const [data, setData] = useState<VoidsCompsData | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const [startDate, setStartDate] = useState<string>(new Date(new Date().setDate(1)).toISOString().split('T')[0])
    const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0])

    useEffect(() => {
        fetchReport()
    }, [restaurantId, startDate, endDate])

    const fetchReport = async () => {
        try {
            setLoading(true)
            const response = await restaurantPortalAPI.getVoidsCompsReport(restaurantId!, startDate, endDate)
            setData(response.data.data)
            setError(null)
        } catch (err: any) {
            console.error("Failed to fetch voids/comps report", err)
            setError("Failed to load report data.")
        } finally {
            setLoading(false)
        }
    }

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "SDG",
        }).format(amount)
    }

    if (loading && !data) {
        return <LoadingSpinner size="3" text="Generating Voids & Comps Record..." />
    }

    if (error) {
        return (
            <Box p="6" className="text-center">
                <Text color="red">{error}</Text>
                <Box mt="4">
                    <button onClick={() => navigate(-1)} className="text-blue-600 underline">Go Back</button>
                </Box>
            </Box>
        )
    }

    if (!data) return null

    return (
        <Box>
            <Box className="print:hidden mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                <Flex gap="6" align="center">
                    <Flex gap="3" align="center">
                        <Label>Start Date:</Label>
                        <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-40"
                        />
                    </Flex>
                    <Flex gap="3" align="center">
                        <Label>End Date:</Label>
                        <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-40"
                        />
                    </Flex>
                </Flex>
            </Box>

            <ReportLayout
                title="Voids & Comps Exception Log"
                subtitle="Security record of cashier overrides, cancelled tickets, and comped items"
                restaurantName={user?.restaurant?.name}
                dateRange={`${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`}
                onBack={() => navigate(`/restaurant/${restaurantId}/reports`)}
            >
                <Card className="mb-8 bg-red-50 dark:bg-red-900/10 print:bg-white print:border print:border-gray-300">
                    <Text size="2" color="red" className="uppercase tracking-wider font-bold mb-1 print:text-gray-600">Total Lost Revenue</Text>
                    <Text size="8" weight="bold" className="text-red-700 print:text-black">{formatCurrency(data.totalLostRevenue)}</Text>
                </Card>

                <Box>
                    <Text size="4" weight="bold" mb="4" className="block border-b pb-2">Exception Tickets</Text>
                    <Table.Root variant="surface" className="print:border print:border-gray-300">
                        <Table.Header className="print:bg-gray-100">
                            <Table.Row>
                                <Table.ColumnHeaderCell>Date & Time</Table.ColumnHeaderCell>
                                <Table.ColumnHeaderCell>Ticket #</Table.ColumnHeaderCell>
                                <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
                                <Table.ColumnHeaderCell>Authorized By</Table.ColumnHeaderCell>
                                <Table.ColumnHeaderCell>Reason</Table.ColumnHeaderCell>
                                <Table.ColumnHeaderCell className="text-right">Lost Amount</Table.ColumnHeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {data.comps.map((comp, i) => (
                                <Table.Row key={`comp-${i}`}>
                                    <Table.Cell className="text-gray-500">
                                        {new Date(comp.date).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                                    </Table.Cell>
                                    <Table.Cell className="font-mono text-xs">{comp.orderNumber}</Table.Cell>
                                    <Table.Cell>
                                        <Badge color={comp.type === 'Voided Order' ? 'red' : 'orange'} variant="soft" className="print:border">
                                            {comp.type}
                                        </Badge>
                                    </Table.Cell>
                                    <Table.Cell className="font-medium">{comp.cashier}</Table.Cell>
                                    <Table.Cell className="text-gray-600 italic">"{comp.reason}"</Table.Cell>
                                    <Table.Cell className="text-right font-bold text-red-600 print:text-black">
                                        {formatCurrency(comp.amount)}
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                            {data.comps.length === 0 && (
                                <Table.Row>
                                    <Table.Cell colSpan={6} className="text-center py-8 text-gray-500">
                                        Excellent! No voids or comps recorded during this period.
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table.Root>
                </Box>
            </ReportLayout>
        </Box>
    )
}

export default VoidsCompsReport
